import FuturesConfigurationFields from '@/features/strategyBacktests/FuturesConfigurationFields';
import { emptyFuturesConfiguration } from '@/features/strategyBacktests/futuresConfigurationDraft';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import {
    Badge,
    Button,
    Callout,
    Card,
    DateTimePicker,
    FormCheckbox,
    FormInput,
    FormNumberInput,
    FormSelect,
    FormSwitch,
    MiniNumberField,
    OptionBuyingStrategySelect,
    SegmentedControl,
    StatList,
    StatRow,
    TradingStrategySelect
} from '@/components/UI';
import { parseNumericDraft, toNumericDraft } from '@/components/UI/numericDraft';
import EntitySelectionPanel from '@/components/UI/EntitySelectionPanel';
import ScreenerUniverseSelector from '@/components/Screeners/ScreenerUniverseSelector';
import ValueSetInput from '@/components/UI/ValueSetInput';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import { formStyles } from '@/utils/formStyles';
import { useCreateAeroOptimizationJobMutation, useTradeLogicAuthoringBackendStatus } from '@/features/tradeLogicAuthoring/api';
import {
    AERO_MAX_HOLD_DAYS,
    AERO_MIN_HOLD_DAYS,
    buildAeroOptimizationJobInputPlan,
    buildAutoPartialTriggerPreview,
    createDefaultAeroOptimizationJobInputState,
    hydrateAeroStateFromJob,
    resolveAeroBaseExitPolicy,
    type AeroBaseExitPolicy,
    type AeroAutoPartialExitState,
    type AeroAutoPartialTriggerPreview,
    type AeroOptimizationJobInputState,
    type AeroPartialExitMode,
    type AeroUniverseMode
} from '@/features/tradeLogicAuthoring/components/aeroOptimizationJobInput';
import { buildAeroParameterSpaceRegistryPlan } from '@/features/tradeLogicAuthoring/components/aeroParameterSpaceRegistry';
import { useTradeLogicActionTemplates } from '@/features/tradeLogicAuthoring/registry';
import {
    buildOwnedAeroStrategies,
    buildOwnedAeroStrategyIds,
    hasPinnedAeroExitVersion,
    parsePositiveInt,
    resolveAeroStrategy,
    type AeroStrategySummary
} from './aeroPageUtils';
import { AERO_PRESETS } from './aeroPresets';
import PartialExitEditor from './components/PartialExitEditor';
import ProfitFloorEditor from './components/ProfitFloorEditor';
import SearchSpaceSummary from './components/SearchSpaceSummary';
import ExitPlanPreview from './components/ExitPlanPreview';

const PARTIAL_EXIT_MODES: { value: AeroPartialExitMode; label: string }[] = [
    { value: 'none', label: 'None' },
    { value: 'manual', label: 'Manual levels' },
    { value: 'auto', label: 'Auto-generate' }
];

const STOP_LOSS_BASIS_OPTIONS: { value: AeroOptimizationJobInputState['stopLossBasis']; label: string }[] = [
    { value: 'openingPremium', label: 'Opening premium' },
    { value: 'requiredCapital', label: 'Required capital' }
];

const UNIVERSE_MODE_OPTIONS: { value: AeroUniverseMode; label: string }[] = [
    { value: 'entities', label: 'Entity list' },
    { value: 'screener', label: 'Screener universe' }
];

const AeroNew = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedStrategyId = parsePositiveInt(searchParams.get('strategyId'));
    const cloneFromId = parsePositiveInt(searchParams.get('cloneFrom'));

    const [futuresDrafts, setFuturesDrafts] = useState(() => [emptyFuturesConfiguration()]);
    const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(requestedStrategyId);
    const [inputState, setInputState] = useState<AeroOptimizationJobInputState>(() => createDefaultAeroOptimizationJobInputState());
    const cloneAppliedRef = useRef(false);

    const { isLoading: isStrategiesLoading, strategies } = useTradingStrategies();

    const accessQuery = useAppQuery({
        ...trpc.resourceAccess.listAccessibleResources.queryOptions({ resourceType: 'tradingStrategy' }),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const cloneJobQuery = useAppQuery({
        ...trpc.exitOptimization.fetchJob.queryOptions({ jobId: cloneFromId ?? 0 }),
        enabled: cloneFromId !== null,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const actionTemplatesQuery = useTradeLogicActionTemplates({ enabled: true });
    const createJobMutation = useCreateAeroOptimizationJobMutation();

    const ownedStrategyIds = useMemo(() => buildOwnedAeroStrategyIds(accessQuery.data), [accessQuery.data]);

    const ownedStrategies = useMemo(
        () =>
            buildOwnedAeroStrategies({
                isAccessLoading: accessQuery.isLoading,
                ownedStrategyIds,
                strategies
            }),
        [accessQuery.isLoading, ownedStrategyIds, strategies]
    );

    const cloneJob = cloneJobQuery.data?.job ?? null;

    useEffect(() => {
        if (cloneFromId === null || cloneAppliedRef.current || cloneJob === null) return;

        cloneAppliedRef.current = true;
        setInputState(hydrateAeroStateFromJob(cloneJob));
        setSelectedStrategyId(cloneJob.strategyId);
    }, [cloneFromId, cloneJob]);

    const selectedStrategy = useMemo(
        () => resolveAeroStrategy({ fallbackToFirst: false, strategies: ownedStrategies, strategyId: selectedStrategyId }),
        [ownedStrategies, selectedStrategyId]
    );

    const usesEtfFutures =
        selectedStrategy?.allowedUnderlyingEntityTypes.includes('Asset') &&
        selectedStrategy.legs.some((leg) => leg.derivativeType === 'Future');
    const strategyAttachmentsQuery = useAppQuery({
        ...trpc.tradeLogicExitCandidates.getStrategyCandidateAttachments.queryOptions({
            strategyId: selectedStrategyId ?? 0
        }),
        enabled: selectedStrategyId !== null
    });
    const baseExitRole = strategyAttachmentsQuery.data?.candidates.find((candidate) => candidate.isBase)?.baseExitRole ?? null;
    const baseExitPolicy = useMemo(
        () => resolveAeroBaseExitPolicy(baseExitRole, inputState.includeStrategyExitComplement),
        [baseExitRole, inputState.includeStrategyExitComplement]
    );
    const effectiveInputState = useMemo<AeroOptimizationJobInputState>(
        () => ({
            ...inputState,
            ...(usesEtfFutures
                ? {
                      futuresConfigurations: futuresDrafts,
                      tradeSizeDollars: '',
                      minOptionPrice: '',
                      priceFillModel: 'observed_close' as const
                  }
                : {}),
            includeStrategyExitComplement: baseExitPolicy.includeStrategyExitComplement
        }),
        [baseExitPolicy.includeStrategyExitComplement, inputState, usesEtfFutures, futuresDrafts]
    );

    const backendStatus = useTradeLogicAuthoringBackendStatus({
        draftKind: 'exit',
        registryManifestFingerprint: null,
        strategyId: selectedStrategyId
    });

    const backendReady = backendStatus.capabilities.queueAeroJob.availability === 'available';

    const registryPlan = useMemo(
        () =>
            buildAeroParameterSpaceRegistryPlan({
                actionTemplates: actionTemplatesQuery.data ?? [],
                isLoading: actionTemplatesQuery.isLoading
            }),
        [actionTemplatesQuery.data, actionTemplatesQuery.isLoading]
    );

    const inputPlan = useMemo(() => {
        const plan = buildAeroOptimizationJobInputPlan({ registryPlan, state: effectiveInputState, strategyId: selectedStrategyId });
        if (!baseExitPolicy.blockingReason) return plan;

        return {
            ...plan,
            input: null,
            blockingReasons: [baseExitPolicy.blockingReason, ...plan.blockingReasons]
        };
    }, [baseExitPolicy.blockingReason, effectiveInputState, registryPlan, selectedStrategyId]);

    const isLoading =
        isStrategiesLoading ||
        accessQuery.isLoading ||
        (selectedStrategyId !== null && strategyAttachmentsQuery.isLoading) ||
        (cloneFromId !== null && cloneJobQuery.isLoading);
    const selectedStrategyUnavailable = selectedStrategyId !== null && !isLoading && selectedStrategy === null;

    const updateInputState = (changes: Partial<AeroOptimizationJobInputState>) => {
        setInputState((currentState) => ({ ...currentState, ...changes }));
    };

    const updateAutoPartial = (changes: Partial<AeroAutoPartialExitState>) => {
        setInputState((currentState) => ({
            ...currentState,
            autoPartialExits: { ...currentState.autoPartialExits, ...changes }
        }));
    };

    const handleStrategyChange = (value: string) => {
        const nextStrategyId = parsePositiveInt(value);
        const nextSearchParams = new URLSearchParams(searchParams);

        if (nextStrategyId === null) nextSearchParams.delete('strategyId');
        else nextSearchParams.set('strategyId', String(nextStrategyId));

        setFuturesDrafts([emptyFuturesConfiguration()]);
        setSelectedStrategyId(nextStrategyId);
        setSearchParams(nextSearchParams);
    };

    const submitJob = async () => {
        if (!backendReady) {
            showToast(backendStatus.capabilities.queueAeroJob.reason ?? 'AERO job queueing is not available.', 'error');
            return;
        }

        if (!inputPlan.input) {
            showToast(inputPlan.blockingReasons[0] ?? 'Complete the required AERO job inputs.', 'error');
            return;
        }

        try {
            const result = await createJobMutation.mutateAsync(inputPlan.input);

            if (!result.success || result.jobId === undefined) {
                showToast(result.errorMessages.join(' ') || 'AERO job was not created.', 'error');
                return;
            }

            showToast(`AERO job #${result.jobId} queued. Monitor it from the job list.`, 'success');
            void navigate('/user/aero');
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'AERO job was not created.', 'error');
        }
    };

    return (
        <UserLayout containerSize="1400px" title="New AERO Job - Project 314">
            <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-2xl font-semibold text-zinc-100">New AERO Job</h1>
                        {cloneJob && <Badge variant="info">Cloned from #{cloneJob.id}</Badge>}
                    </div>
                    <p className="mt-1 text-sm text-zinc-400">
                        Search exit candidates while preserving each strategy's required exit behavior.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button linkProps={{ reloadDocument: true }} size="sm" to="/user/aero" variant="outline-dark">
                        Job List
                    </Button>
                    {selectedStrategyId !== null && (
                        <Button size="sm" to={`/user/tradingStrategy/${selectedStrategyId}`} variant="outline-dark">
                            Strategy
                        </Button>
                    )}
                </div>
            </div>

            {isLoading ? (
                <Card>
                    <DataLoadingSpinner text="Loading AERO inputs..." />
                </Card>
            ) : selectedStrategyUnavailable ? (
                <Card>
                    <div className="text-sm text-amber-100">The selected strategy is unavailable or not owned by this account.</div>
                </Card>
            ) : ownedStrategies.length === 0 ? (
                <Card>
                    <div className="text-sm text-zinc-400">No owned trading strategies are available for AERO.</div>
                </Card>
            ) : (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
                    <div className="space-y-4">
                        <JobSetupCard
                            backendReady={backendReady}
                            inputState={inputState}
                            ownedStrategies={ownedStrategies}
                            selectedStrategy={selectedStrategy}
                            selectedStrategyId={selectedStrategyId}
                            onStrategyChange={handleStrategyChange}
                            onUpdateInput={updateInputState}
                        />

                        <AeroUniverseCard
                            effectiveStrategyScreenerRuleId={selectedStrategy?.activeScreenerRuleId ?? null}
                            inputState={inputState}
                            onUpdateInput={updateInputState}
                        />

                        <ExitStrategyCard
                            inputState={inputState}
                            onUpdateAutoPartial={updateAutoPartial}
                            onUpdateInput={updateInputState}
                        />

                        {usesEtfFutures && selectedStrategyId !== null && (
                            <Card
                                title="ETF signals → native futures"
                                subtitle="Universe qualification and entries use the ETF. Dated futures determine fills, costs and exits. Each child remains limited to 100,000 decision minutes."
                            >
                                <div className="flex flex-col gap-4">
                                    {futuresDrafts.map((draft, index) => (
                                        <div key={index}>
                                            <FuturesConfigurationFields
                                                value={draft}
                                                strategyIds={[selectedStrategyId]}
                                                onChange={(value) =>
                                                    setFuturesDrafts((current) => current.map((item, i) => (i === index ? value : item)))
                                                }
                                            />
                                            {futuresDrafts.length > 1 && (
                                                <Button
                                                    onClick={() => setFuturesDrafts((current) => current.filter((_, i) => i !== index))}
                                                >
                                                    Remove route
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    <Button
                                        disabled={futuresDrafts.length >= 2}
                                        onClick={() => setFuturesDrafts((current) => [...current, emptyFuturesConfiguration()])}
                                    >
                                        Add route
                                    </Button>
                                </div>
                                <p className="mt-3 text-sm text-zinc-400">
                                    Mandatory session and roll closes bound every position. Failed-unit tolerance is zero and native trade
                                    receipts are retained.
                                </p>
                            </Card>
                        )}
                        <RunOptionsCard baseExitPolicy={baseExitPolicy} inputState={effectiveInputState} onUpdateInput={updateInputState} />
                    </div>

                    <RunSummaryCard
                        backendReady={backendReady}
                        inputPlan={inputPlan}
                        inputState={inputState}
                        isCreating={createJobMutation.isPending}
                        selectedStrategy={selectedStrategy}
                        onSubmit={submitJob}
                    />
                </div>
            )}
        </UserLayout>
    );
};

/* -------------------------------------------------------------------------- */
/* Job setup                                                                  */
/* -------------------------------------------------------------------------- */

const JobSetupCard = ({
    backendReady,
    inputState,
    ownedStrategies,
    selectedStrategy,
    selectedStrategyId,
    onStrategyChange,
    onUpdateInput
}: {
    backendReady: boolean;
    inputState: AeroOptimizationJobInputState;
    ownedStrategies: AeroStrategySummary[];
    selectedStrategy: AeroStrategySummary | null;
    selectedStrategyId: number | null;
    onStrategyChange: (value: string) => void;
    onUpdateInput: (changes: Partial<AeroOptimizationJobInputState>) => void;
}) => (
    <Card
        headerActions={
            <div className="flex flex-wrap gap-2">
                <Badge variant={backendReady ? 'success' : 'warning'}>{backendReady ? 'Queue ready' : 'Queue unavailable'}</Badge>
                <Badge variant={hasPinnedAeroExitVersion(selectedStrategy) ? 'success' : 'dark'}>
                    {hasPinnedAeroExitVersion(selectedStrategy) ? 'Exit version pinned' : 'No exit version pinned'}
                </Badge>
            </div>
        }
        subtitle="Pick the strategy to optimize and name the run."
        title="Job Setup"
    >
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(260px,1fr)_minmax(220px,1fr)]">
            <TradingStrategySelect
                filter={(strategy) => ownedStrategies.some((owned) => owned.id === strategy.id)}
                label="Strategy"
                placeholder="Select strategy..."
                value={selectedStrategyId}
                onChange={(strategyId) => onStrategyChange(strategyId === null ? '' : String(strategyId))}
            />
            <FormInput
                label="Job Name"
                placeholder="Optional run name"
                value={inputState.jobName}
                onChange={(event) => onUpdateInput({ jobName: event.target.value })}
            />
        </div>
        <TextareaField
            label="Description"
            placeholder="Optional notes for this optimization run"
            value={inputState.description}
            onChange={(value) => onUpdateInput({ description: value })}
        />
    </Card>
);

/* -------------------------------------------------------------------------- */
/* Universe                                                                   */
/* -------------------------------------------------------------------------- */

const AeroUniverseCard = ({
    effectiveStrategyScreenerRuleId,
    inputState,
    onUpdateInput
}: {
    effectiveStrategyScreenerRuleId: number | null;
    inputState: AeroOptimizationJobInputState;
    onUpdateInput: (changes: Partial<AeroOptimizationJobInputState>) => void;
}) => (
    <Card
        subtitle="Choose the entities AERO can simulate. Entry signals always use the selected strategy's attached screener."
        title="Base Universe & Strategy Screener"
    >
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm font-medium text-zinc-300">Base Universe Source</div>
            <SegmentedControl
                options={UNIVERSE_MODE_OPTIONS}
                value={inputState.universeMode}
                onChange={(universeMode) => onUpdateInput({ universeMode })}
            />
        </div>

        {inputState.universeMode === 'entities' ? (
            <div className="space-y-4">
                <EntitySelectionPanel
                    entityListId={inputState.entityListId}
                    selectedEntities={inputState.entities}
                    onEntitiesChange={(entities) => onUpdateInput({ entities })}
                    onEntityListIdChange={(entityListId) => onUpdateInput({ entityListId })}
                />
                <div className="rounded-md border border-zinc-800 bg-black/20 p-3 text-xs text-zinc-500">
                    {effectiveStrategyScreenerRuleId === null
                        ? 'This strategy has no attached screener, so AERO will not gate generated entries.'
                        : `AERO will gate generated entries with attached strategy screener #${effectiveStrategyScreenerRuleId}.`}
                </div>
            </div>
        ) : (
            <ScreenerUniverseSelector
                entityTypes={inputState.screenerEntityTypes}
                screenerRuleId={inputState.screenerRuleId}
                selectHelpText="This screener selects candidate entities only. Entry signals still use the strategy's attached screener."
                selectLabel="Universe Screener"
                onEntityTypesChange={(screenerEntityTypes) => onUpdateInput({ screenerEntityTypes })}
                onScreenerRuleIdChange={(screenerRuleId) => onUpdateInput({ screenerRuleId, screenerEntityTypes: [] })}
            />
        )}

        <div className="mt-4 rounded-md border border-zinc-800 bg-black/20 p-3 text-xs text-zinc-400">
            Stocks and indexes can share an entity list. Index options use the selected option buying policy and the same spread and quote
            checks as stock options. Index option trades must close before the contract’s last trading time; expiration settlement is
            unsupported. Include a pre-expiration safety exit in the strategy. Universe screeners currently return stocks and
            cryptocurrencies; select indexes directly or through an entity list.
        </div>
        <div className="mt-4">
            <DateTimePicker
                endDateTime={inputState.endDateTime}
                startDateTime={inputState.startDateTime}
                onDateRangeChange={(startDateTime, endDateTime) => onUpdateInput({ startDateTime, endDateTime })}
            />
        </div>
    </Card>
);

/* -------------------------------------------------------------------------- */
/* Exit strategy                                                              */
/* -------------------------------------------------------------------------- */

const ExitStrategyCard = ({
    inputState,
    onUpdateAutoPartial,
    onUpdateInput
}: {
    inputState: AeroOptimizationJobInputState;
    onUpdateAutoPartial: (changes: Partial<AeroAutoPartialExitState>) => void;
    onUpdateInput: (changes: Partial<AeroOptimizationJobInputState>) => void;
}) => (
    <Card subtitle="Turn on the exit behaviours to test. At least one is required." title="Exit Strategy">
        <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-zinc-500">Start from a preset</span>
            {AERO_PRESETS.map((preset) => (
                <button
                    className="rounded-md border border-zinc-700 px-2.5 py-1 text-xs font-medium text-zinc-300 hover:border-cyan-600 hover:text-cyan-300"
                    key={preset.id}
                    title={preset.description}
                    type="button"
                    onClick={() => onUpdateInput(preset.values)}
                >
                    {preset.label}
                </button>
            ))}
        </div>

        <div className="space-y-3">
            <ParameterModule
                description="Limit the loss AERO will accept on each trade."
                enabled={inputState.stopLossEnabled}
                title="Stop loss"
                onToggle={(enabled) => onUpdateInput({ stopLossEnabled: enabled })}
            >
                <div>
                    <div className="mb-1.5 text-sm font-medium text-zinc-200">Loss percentage basis</div>
                    <SegmentedControl
                        options={STOP_LOSS_BASIS_OPTIONS}
                        value={inputState.stopLossBasis}
                        onChange={(stopLossBasis) =>
                            onUpdateInput({
                                stopLossBasis,
                                ...(stopLossBasis === 'requiredCapital' ? { trailingStopEnabled: false } : {})
                            })
                        }
                    />
                    <p className="mt-1.5 text-xs text-zinc-500">
                        Opening premium preserves existing behavior. Required capital is intended for fixed, cash-secured sell-to-open risk
                        limits.
                    </p>
                </div>
                <ValueSetInput
                    minExclusive
                    helpText="Each percentage is one stop-loss variant to test."
                    label="Test these stop-loss levels"
                    min={0}
                    placeholder="e.g. 5"
                    unit="%"
                    values={inputState.stopLossPercentages}
                    onChange={(values) => onUpdateInput({ stopLossPercentages: values })}
                />
                <FormCheckbox
                    checked={inputState.trailingStopEnabled}
                    disabled={inputState.stopLossBasis === 'requiredCapital'}
                    helpText={
                        inputState.stopLossBasis === 'requiredCapital'
                            ? 'Required-capital stops are fixed stops.'
                            : "A trailing stop follows the trade's high once it is in profit."
                    }
                    label="Also test a trailing stop"
                    labelClassName="text-zinc-300"
                    onChange={(event) => onUpdateInput({ trailingStopEnabled: event.target.checked })}
                />
                {inputState.trailingStopEnabled && (
                    <div className="grid grid-cols-1 gap-3 rounded-md border border-zinc-800 bg-black/20 p-3 lg:grid-cols-2">
                        <ValueSetInput
                            helpText="Profit needed before the trail activates."
                            label="Activation profit"
                            min={0}
                            placeholder="e.g. 8"
                            unit="%"
                            values={inputState.trailingActivationProfits}
                            onChange={(values) => onUpdateInput({ trailingActivationProfits: values })}
                        />
                        <ValueSetInput
                            minExclusive
                            helpText="How far price can pull back from the high."
                            label="Trail by"
                            min={0}
                            placeholder="e.g. 3"
                            unit="%"
                            values={inputState.trailingByPercentages}
                            onChange={(values) => onUpdateInput({ trailingByPercentages: values })}
                        />
                    </div>
                )}
            </ParameterModule>

            <ParameterModule
                description="Take profit once a trade reaches a gain."
                enabled={inputState.profitTargetEnabled}
                title="Profit target"
                onToggle={(enabled) => onUpdateInput({ profitTargetEnabled: enabled })}
            >
                <ValueSetInput
                    minExclusive
                    helpText="Each percentage is one profit-target variant to test."
                    label="Test these profit-target levels"
                    min={0}
                    placeholder="e.g. 15"
                    unit="%"
                    values={inputState.profitTargetPercentages}
                    onChange={(values) => onUpdateInput({ profitTargetPercentages: values })}
                />

                <div>
                    <div className="mb-1.5 text-sm font-medium text-zinc-200">Partial exits</div>
                    <p className="mb-2 text-xs text-zinc-500">Optionally scale out of the position before the full target is hit.</p>
                    <SegmentedControl
                        options={PARTIAL_EXIT_MODES}
                        value={inputState.partialExitMode}
                        onChange={(value) => onUpdateInput({ partialExitMode: value })}
                    />

                    {inputState.partialExitMode === 'manual' && (
                        <div className="mt-3">
                            <PartialExitEditor
                                levels={inputState.manualPartialExits}
                                onChange={(levels) => onUpdateInput({ manualPartialExits: levels })}
                            />
                        </div>
                    )}

                    {inputState.partialExitMode === 'auto' && (
                        <div className="mt-3">
                            <AutoPartialExitFields value={inputState.autoPartialExits} onChange={onUpdateAutoPartial} />
                        </div>
                    )}
                </div>
            </ParameterModule>

            <ParameterModule
                description="Lock in gains if a trade runs up and then gives them back."
                enabled={inputState.profitFloorEnabled}
                title="Profit floor"
                onToggle={(enabled) => onUpdateInput({ profitFloorEnabled: enabled })}
            >
                <ProfitFloorEditor floors={inputState.profitFloors} onChange={(floors) => onUpdateInput({ profitFloors: floors })} />
            </ParameterModule>

            <ParameterModule
                description="Exit after a selected number of completed strategy-duration bars."
                enabled={inputState.maxHoldBarsEnabled}
                title="Maximum hold bars"
                onToggle={(enabled) => onUpdateInput({ maxHoldBarsEnabled: enabled })}
            >
                <ValueSetInput
                    integer
                    minExclusive
                    helpText="Each whole number is one strategy-duration time stop to test."
                    label="Test these maximum hold bars"
                    min={0}
                    placeholder="e.g. 4"
                    unit="bars"
                    values={inputState.maxHoldBars}
                    onChange={(values) => onUpdateInput({ maxHoldBars: values })}
                />
                <FormCheckbox
                    checked={inputState.includeNoTimeStop}
                    helpText="Adds one control candidate that relies on the other exits without an optimizer-owned bar limit."
                    label="Also test no time stop"
                    labelClassName="text-zinc-300"
                    onChange={(event) => onUpdateInput({ includeNoTimeStop: event.target.checked })}
                />
            </ParameterModule>
        </div>
    </Card>
);

const ParameterModule = ({
    title,
    description,
    enabled,
    onToggle,
    children
}: {
    title: string;
    description: string;
    enabled: boolean;
    onToggle: (enabled: boolean) => void;
    children: ReactNode;
}) => (
    <div className={`rounded-lg border ${enabled ? 'border-zinc-700' : 'border-zinc-800'} bg-black/20`}>
        <div className="flex items-center justify-between gap-4 px-4 py-3">
            <div>
                <div className="text-sm font-semibold text-zinc-100">{title}</div>
                <div className="text-xs text-zinc-500">{description}</div>
            </div>
            <FormSwitch checked={enabled} className="!mb-0" onChange={(event) => onToggle(event.target.checked)} />
        </div>
        {enabled && <div className="space-y-4 border-t border-zinc-800 px-4 py-4">{children}</div>}
    </div>
);

const AutoPartialExitFields = ({
    value,
    onChange
}: {
    value: AeroAutoPartialExitState;
    onChange: (changes: Partial<AeroAutoPartialExitState>) => void;
}) => (
    <div className="space-y-3 rounded-md border border-zinc-800 bg-black/20 p-3">
        <ValueSetInput
            minExclusive
            helpText="Slice sizes for partial-exit ladders. Type each and press Enter, or use Range…."
            label="Exit percentages to test"
            min={0}
            placeholder="e.g. 25"
            unit="%"
            values={value.exitPercentages}
            onChange={(values) => onChange({ exitPercentages: values })}
        />
        <div>
            <div className="mb-1 text-sm font-medium text-zinc-200">Profit range for partial-exit triggers</div>
            <div className="flex flex-wrap items-end gap-2">
                <MiniNumberField
                    label="From %"
                    placeholder="5"
                    value={value.atProfitFrom}
                    onChange={(next) => onChange({ atProfitFrom: next })}
                />
                <MiniNumberField
                    label="To %"
                    placeholder="20"
                    value={value.atProfitTo}
                    onChange={(next) => onChange({ atProfitTo: next })}
                />
                <MiniNumberField
                    label="Step %"
                    placeholder="5"
                    value={value.atProfitStep}
                    onChange={(next) => onChange({ atProfitStep: next })}
                />
            </div>
            <AutoPartialTriggerPreview preview={buildAutoPartialTriggerPreview(value)} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FormSelect
                noMargin
                label="Max levels"
                options={[
                    { label: '1 level', value: '1' },
                    { label: '2 levels', value: '2' },
                    { label: '3 levels', value: '3' }
                ]}
                value={String(value.maxLevels)}
                onChange={(event) => onChange({ maxLevels: Number(event.target.value) })}
            />
            <FormNumberInput
                noMargin
                label="Min spacing %"
                placeholder="Optional"
                value={parseNumericDraft(value.minSpacingPercent)}
                onValueChange={(numericValue) => onChange({ minSpacingPercent: toNumericDraft(numericValue) })}
            />
            <FormNumberInput
                noMargin
                helpText="1-1000"
                label="Cap per target"
                placeholder="200"
                value={parseNumericDraft(value.capPerTarget)}
                onValueChange={(numericValue) => onChange({ capPerTarget: toNumericDraft(numericValue) })}
            />
        </div>
        <FormCheckbox
            checked={value.includeRemainderAtTarget}
            label="Include a final exit at the profit target"
            labelClassName="text-zinc-300"
            onChange={(event) => onChange({ includeRemainderAtTarget: event.target.checked })}
        />
    </div>
);

const AUTO_PARTIAL_TRIGGER_PREVIEW_LIMIT = 40;

const AutoPartialTriggerPreview = ({ preview }: { preview: AeroAutoPartialTriggerPreview }) => {
    if (preview.error) {
        return <div className="mt-2 text-xs text-amber-300">{preview.error}</div>;
    }

    if (!preview.started) {
        return (
            <div className="mt-2 text-xs text-zinc-500">
                Enter from, to, and step — AERO expands this range into the trigger levels it tests. No generate step needed.
            </div>
        );
    }

    if (preview.triggers.length === 0) {
        return <div className="mt-2 text-xs text-zinc-500">This range produces no trigger points.</div>;
    }

    const shownTriggers = preview.triggers.slice(0, AUTO_PARTIAL_TRIGGER_PREVIEW_LIMIT);
    const hiddenCount = preview.triggers.length - shownTriggers.length;

    return (
        <div className="mt-2">
            <div className="mb-1 text-xs text-zinc-500">
                AERO will test partial exits at these {preview.triggers.length} profit level
                {preview.triggers.length === 1 ? '' : 's'}
                {preview.truncated ? ' (preview clipped)' : ''}:
            </div>
            <div className="flex flex-wrap gap-1">
                {shownTriggers.map((trigger) => (
                    <span className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-xs text-zinc-400" key={trigger}>
                        {trigger}%
                    </span>
                ))}
                {hiddenCount > 0 && <span className="px-1 py-0.5 text-xs text-zinc-500">+{hiddenCount} more</span>}
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Run options                                                                */
/* -------------------------------------------------------------------------- */

const RunOptionsCard = ({
    baseExitPolicy,
    inputState,
    onUpdateInput
}: {
    baseExitPolicy: AeroBaseExitPolicy;
    inputState: AeroOptimizationJobInputState;
    onUpdateInput: (changes: Partial<AeroOptimizationJobInputState>) => void;
}) => {
    return (
        <Card subtitle="Sizing, holding period, and run behaviour." title="Run Options">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                <div className="lg:col-span-1">
                    <OptionBuyingStrategySelect
                        noMargin
                        helpText={
                            inputState.optionBuyingStrategyIds.length === 0
                                ? 'Stock-only simulation.'
                                : `${inputState.optionBuyingStrategyIds.length} option strategy ${
                                      inputState.optionBuyingStrategyIds.length === 1 ? 'axis' : 'axes'
                                  } selected.`
                        }
                        label="Option Buying Strategies"
                        mode="multi"
                        value={inputState.optionBuyingStrategyIds}
                        onChange={(optionBuyingStrategyIds) => onUpdateInput({ optionBuyingStrategyIds })}
                    />
                </div>
                <FormSelect
                    noMargin
                    disabled={inputState.optionBuyingStrategyIds.length === 0}
                    helpText={
                        inputState.optionBuyingStrategyIds.length === 0
                            ? 'Stock jobs always use raw returns.'
                            : 'Frozen when the experiment starts. Both raw and winsorized results are still retained and shown.'
                    }
                    label="Option return selection"
                    options={[
                        { label: 'Winsorized (recommended)', value: 'upper_winsorized_v1' },
                        { label: 'Raw returns', value: 'raw_v1' }
                    ]}
                    value={inputState.optionReturnSelectionPolicy}
                    onChange={(event) =>
                        onUpdateInput({
                            optionReturnSelectionPolicy: event.target.value as AeroOptimizationJobInputState['optionReturnSelectionPolicy']
                        })
                    }
                />
                <FormSelect
                    noMargin
                    helpText={
                        inputState.futuresConfigurations
                            ? 'Conditions use native futures bars; fills require the exact dated execution-minute open.'
                            : inputState.priceFillModel === 'resting_level'
                              ? 'Uses each minute open for gaps and the exact threshold for range touches. Stock jobs only.'
                              : 'Preserves the current model: a triggered exit fills at the observed minute close.'
                    }
                    label="Exit price fill model"
                    disabled={!!inputState.futuresConfigurations}
                    options={[
                        { label: 'Observed close (current)', value: 'observed_close' },
                        { label: 'Resting level', value: 'resting_level' }
                    ]}
                    value={inputState.priceFillModel}
                    onChange={(event) =>
                        onUpdateInput({
                            priceFillModel: event.target.value as AeroOptimizationJobInputState['priceFillModel']
                        })
                    }
                />
                <FormNumberInput
                    helpText={
                        inputState.futuresConfigurations
                            ? 'Sizing uses the selected native futures allocation.'
                            : 'Optional fixed trade sizing.'
                    }
                    label="Trade Size Dollars"
                    disabled={!!inputState.futuresConfigurations}
                    placeholder="Optional"
                    value={parseNumericDraft(inputState.tradeSizeDollars)}
                    onValueChange={(numericValue) => onUpdateInput({ tradeSizeDollars: toNumericDraft(numericValue) })}
                />
                <FormNumberInput
                    helpText={`${AERO_MIN_HOLD_DAYS}-${AERO_MAX_HOLD_DAYS} days.`}
                    label="Max Hold Days"
                    disabled={!!inputState.futuresConfigurations}
                    max={AERO_MAX_HOLD_DAYS}
                    min={AERO_MIN_HOLD_DAYS}
                    value={inputState.futuresConfigurations ? 0 : parseNumericDraft(inputState.maxTradeDurationDays)}
                    onValueChange={(numericValue) => onUpdateInput({ maxTradeDurationDays: toNumericDraft(numericValue) })}
                />
                <FormNumberInput
                    helpText="Percent of work units allowed to fail (after AERO's retry sweep) without failing the job. Empty uses the server default; 0 is strict."
                    label="Failed-Unit Tolerance %"
                    disabled={!!inputState.futuresConfigurations}
                    max={100}
                    min={0}
                    placeholder="Server default"
                    value={inputState.futuresConfigurations ? 0 : parseNumericDraft(inputState.maxFailedUnitPercent)}
                    onValueChange={(numericValue) => onUpdateInput({ maxFailedUnitPercent: toNumericDraft(numericValue) })}
                />
                <FormNumberInput
                    helpText="Minimum executable option price the finder may select. Empty means no job-level floor; the option strategy's own minimum price still applies."
                    label="Min Option Price $"
                    disabled={!!inputState.futuresConfigurations}
                    min={0}
                    placeholder="No floor"
                    value={parseNumericDraft(inputState.minOptionPrice)}
                    onValueChange={(numericValue) => onUpdateInput({ minOptionPrice: toNumericDraft(numericValue) })}
                />
            </div>
            <div className="mt-2 flex flex-col gap-3">
                <FormCheckbox
                    checked={baseExitPolicy.includeStrategyExitComplement}
                    disabled={!baseExitPolicy.canChoose}
                    helpText={baseExitPolicy.helpText}
                    label="Include fixed strategy exit rules"
                    labelClassName="text-zinc-300"
                    onChange={(event) => onUpdateInput({ includeStrategyExitComplement: event.target.checked })}
                />
                <FormCheckbox
                    checked={inputState.futuresConfigurations ? false : inputState.deleteTradesAfterCompletion}
                    label="Delete generated trades after completion"
                    disabled={!!inputState.futuresConfigurations}
                    labelClassName="text-zinc-300"
                    onChange={(event) => onUpdateInput({ deleteTradesAfterCompletion: event.target.checked })}
                />
                <FormCheckbox
                    checked={inputState.enableDebugLogging}
                    label="Enable debug logging"
                    labelClassName="text-zinc-300"
                    onChange={(event) => onUpdateInput({ enableDebugLogging: event.target.checked })}
                />
            </div>
        </Card>
    );
};

/* -------------------------------------------------------------------------- */
/* Run summary                                                                */
/* -------------------------------------------------------------------------- */

const RunSummaryCard = ({
    backendReady,
    inputPlan,
    inputState,
    isCreating,
    selectedStrategy,
    onSubmit
}: {
    backendReady: boolean;
    inputPlan: ReturnType<typeof buildAeroOptimizationJobInputPlan>;
    inputState: AeroOptimizationJobInputState;
    isCreating: boolean;
    selectedStrategy: AeroStrategySummary | null;
    onSubmit: () => void;
}) => (
    <div className="space-y-4 xl:sticky xl:top-4 xl:self-start">
        <Card
            footer={
                <Button
                    fullWidth
                    disabled={!backendReady || inputPlan.input === null || isCreating}
                    isLoading={isCreating}
                    onClick={onSubmit}
                >
                    Queue AERO Job
                </Button>
            }
            subtitle="AERO simulates every exit combination and ranks the candidates."
            title="Run Summary"
        >
            <StatList className="mb-3">
                <StatRow
                    label="Strategy"
                    value={selectedStrategy ? `${selectedStrategy.name} (#${selectedStrategy.id})` : 'None selected'}
                />
                <StatRow
                    label="Base universe"
                    value={
                        inputState.universeMode === 'screener'
                            ? inputState.screenerRuleId === null
                                ? 'No universe screener'
                                : `Screener candidates (#${inputState.screenerRuleId})`
                            : `${inputState.entities.length} entities`
                    }
                />
                <StatRow label="Strategy screener" value={describeAeroEntryGate(selectedStrategy)} />
            </StatList>

            <SearchSpaceSummary searchSpace={inputPlan.searchSpace} />

            {inputPlan.blockingReasons.length > 0 && (
                <Callout className="mt-4" title="Needs attention" variant="warning">
                    <ul className="space-y-1">
                        {inputPlan.blockingReasons.map((reason) => (
                            <li key={reason}>• {reason}</li>
                        ))}
                    </ul>
                </Callout>
            )}

            {!backendReady && (
                <Callout className="mt-4" variant="warning">
                    The AERO orchestrator is not reachable. Confirm the Rust AERO service is running.
                </Callout>
            )}
        </Card>

        <Card subtitle="A representative trade under this configuration." title="Exit Plan Preview">
            <ExitPlanPreview state={inputState} />
        </Card>
    </div>
);

const describeAeroEntryGate = (strategy: AeroStrategySummary | null): string =>
    strategy?.activeScreenerRuleId == null ? 'No attached screener' : `Strategy screener #${strategy.activeScreenerRuleId}`;

/* -------------------------------------------------------------------------- */
/* Small shared inputs                                                        */
/* -------------------------------------------------------------------------- */

const TextareaField = ({
    label,
    placeholder,
    value,
    onChange
}: {
    label: string;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
}) => {
    const inputId = useId();

    return (
        <div className="mt-4 w-full">
            <label className={formStyles.label} htmlFor={inputId}>
                {label}
            </label>
            <textarea
                className={`${formStyles.textarea} ${formStyles.placeholder}`}
                id={inputId}
                placeholder={placeholder}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        </div>
    );
};

export default AeroNew;
